/** Iter 106 — full edit of a manual booking (dates, vehicle handled via swap,
 * locations, price, insurance, extras w/ qty, custom charges, deposits,
 * status, notes, customer contact). Totals recompute server-side; every
 * change lands in the booking's audit history. */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, History } from "lucide-react";
import { ManualBookingPriceDetails } from "@/components/booking/ManualBookingPriceDetails";
import { ManualBookingExtrasEditor } from "@/components/booking/ManualBookingExtrasEditor";
import { emptyPricingSpec, buildPricingPayload } from "@/lib/manualPricing";

export default function ManualBookingEditDialog({ booking, open, onClose, onSaved }) {
  const [car, setCar] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [extras, setExtras] = useState([]);
  const [spec, setSpec] = useState(() => emptyPricingSpec());
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!open || !booking) return;
    const cd = booking.customer_details || {};
    setF({
      start_date: (booking.start_date || "").slice(0, 10),
      end_date: (booking.end_date || "").slice(0, 10),
      pickup_time: booking.pickup_time || "",
      return_time: booking.return_time || "",
      pickup_location: booking.pickup_location || "",
      dropoff_location: booking.dropoff_location || "",
      insurance_plan_id: booking.insurance_plan_id || "",
      status: booking.status || "confirmed",
      payment_status: booking.payment_status || "pending",
      notes: booking.notes || "",
      customer_notes: booking.customer_notes || "",
      customer_name: cd.full_name || booking.customer?.name || "",
      customer_email: cd.email || booking.customer?.email || "",
      customer_phone: cd.phone || booking.customer?.phone || "",
    });
    setExtras((booking.add_ons || []).map((a) => ({
      id: a.id || null, name: a.name, price: a.price ?? 0, pricing: a.pricing || "per_day",
      qty: a.qty || 1, free: !!a.free, custom: !!a.custom, catalogPrice: a.price,
    })));
    const md = booking.manual_discount || {};
    const hadRentalOverride = (booking.price_audit || []).some((a) => a.field === "rental_subtotal");
    setSpec({
      ...emptyPricingSpec(),
      rental_subtotal_override: hadRentalOverride ? String(booking.rental_subtotal ?? "") : "",
      additional_charges: (booking.custom_charges || []).map((c) => ({ label: c.label, amount: String(c.amount) })),
      discount_type: md.type || "fixed",
      discount_value: md.value ? String(md.value) : "",
      vat_rate_pct: booking.vat_rate_pct ? String(booking.vat_rate_pct) : "",
      deposit_override: booking.deposit != null ? String(booking.deposit) : "",
      // Legacy bookings included the deposit in the total — keep their totals stable.
      deposit_in_total: booking.deposit_in_total ?? (Number(booking.deposit) > 0),
    });
    setHistory(null);
    setQuote(null);
    api.get(`/cars/${booking.car_id}`).then((r) => setCar(r.data)).catch(() => setCar(null));
  }, [open, booking]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const days = useMemo(() => {
    if (!f?.start_date || !f?.end_date) return 0;
    return Math.max(0, Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000));
  }, [f?.start_date, f?.end_date]);

  // Live base quote (dates / locations / insurance changes recalc per-day items).
  useEffect(() => {
    if (!open || !booking || !f?.start_date || !f?.end_date || days < 1) return;
    const t = setTimeout(() => {
      api.post(`/cars/${booking.car_id}/quote`, {
        start_date: new Date(`${f.start_date}T00:00:00Z`).toISOString(),
        end_date: new Date(`${f.end_date}T00:00:00Z`).toISOString(),
        pickup_location: f.pickup_location || null,
        dropoff_location: f.dropoff_location || null,
        insurance_plan_id: f.insurance_plan_id || null,
        add_on_ids: [],
      }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, f?.start_date, f?.end_date, f?.pickup_location, f?.dropoff_location, f?.insurance_plan_id]);

  const priceBase = useMemo(() => ({
    days: Math.max(1, days),
    rental_subtotal: quote?.subtotal ?? booking?.rental_subtotal ?? 0,
    route_fee: quote?.route_fee ?? booking?.route_fee ?? 0,
    meet_greet_fee: quote?.meet_greet_fee ?? booking?.meet_greet_fee ?? 0,
    insurance_fee: quote?.insurance_fee ?? booking?.insurance_fee ?? 0,
    deposit: quote?.deposit ?? booking?.deposit ?? 0,
  }), [quote, booking, days]);

  const paidSoFar = useMemo(() => {
    let p = Number(booking?.amount_paid_online || 0);
    for (const m of booking?.manual_payments || []) {
      if (m.status !== "voided" && ["rental_balance", "partial_payment"].includes(m.payment_type)) p += Number(m.amount || 0);
    }
    return Math.round(p * 100) / 100;
  }, [booking]);

  const datesChanged = f && (f.start_date !== (booking.start_date || "").slice(0, 10) || f.end_date !== (booking.end_date || "").slice(0, 10));
  const hasOverrides = spec.rental_subtotal_override !== "" || spec.total_override !== "" || spec.insurance_price_override !== "" || spec.route_fee_override !== "";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/bookings/${booking.id}/manual-edit`, {
        start_date: f.start_date, end_date: f.end_date,
        pickup_time: f.pickup_time, return_time: f.return_time,
        pickup_location: f.pickup_location, dropoff_location: f.dropoff_location,
        insurance_plan_id: f.insurance_plan_id,
        pricing: buildPricingPayload(spec, extras),
        status: f.status, payment_status: f.payment_status,
        notes: f.notes, customer_notes: f.customer_notes,
        customer_name: f.customer_name, customer_email: f.customer_email, customer_phone: f.customer_phone,
      });
      toast.success(<span>Booking updated — new total <Money amount={data.booking.amount} /></span>);
      onSaved?.(data.booking);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const loadHistory = () => {
    api.get(`/bookings/${booking.id}/edit-history`).then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  };

  if (!f) return null;
  const plans = car?.insurance_plans || [];
  const addOns = car?.add_ons || [];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit booking #{(booking.id || "").slice(0, 8).toUpperCase()}</DialogTitle>
          <DialogDescription>Totals recalculate automatically. Every change is kept in the audit history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><Label>Start</Label><Input type="date" value={f.start_date} onChange={set("start_date")} className="mt-1" data-testid="edit-start-date" /></div>
            <div><Label>End</Label><Input type="date" value={f.end_date} onChange={set("end_date")} className="mt-1" data-testid="edit-end-date" /></div>
            <div><Label>Pickup time</Label><Input value={f.pickup_time} onChange={set("pickup_time")} placeholder="10:00" className="mt-1" data-testid="edit-pickup-time" /></div>
            <div><Label>Return time</Label><Input value={f.return_time} onChange={set("return_time")} placeholder="10:00" className="mt-1" data-testid="edit-return-time" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Pickup location</Label>
              <select value={f.pickup_location} onChange={set("pickup_location")} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="edit-pickup-location">
                {(car?.pickup_locations || [car?.location].filter(Boolean)).map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>Drop-off location</Label>
              <select value={f.dropoff_location} onChange={set("dropoff_location")} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="edit-dropoff-location">
                {(car?.dropoff_locations || [car?.location].filter(Boolean)).map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
          </div>

          {plans.length > 0 && (
            <div>
              <Label>Insurance</Label>
              <div className="mt-1 space-y-1.5" data-testid="edit-insurance-section">
                {plans.map((p) => (
                  <button key={p.id} type="button" onClick={() => set("insurance_plan_id")(f.insurance_plan_id === p.id ? "" : p.id)}
                    className={`w-full text-left rounded-lg border px-3 py-2 flex justify-between text-sm ${f.insurance_plan_id === p.id ? "border-blue-500 bg-blue-50/40" : "border-border"}`}
                    data-testid={`edit-insurance-${p.id}`}>
                    <span>{p.name}</span><span className="tabular-nums"><Money amount={p.price_per_day} />/day{days ? ` × ${days}d` : ""}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-neutral-500">Override insurance total (€):</span>
                <Input type="number" inputMode="decimal" value={spec.insurance_price_override}
                  onChange={(e) => setSpec((s) => ({ ...s, insurance_price_override: e.target.value }))}
                  placeholder="auto" className="h-8 w-28 rounded-lg text-sm" data-testid="edit-insurance-override" />
              </div>
            </div>
          )}

          <ManualBookingExtrasEditor catalog={addOns} extras={extras} setExtras={setExtras} days={Math.max(1, days)} />

          {datesChanged && hasOverrides && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="override-recalc-warning">
              Dates changed: per-day extras and insurance recalculate automatically, but your manual price
              overrides stay as entered. Clear an override field to let it recalculate.
            </div>
          )}

          <ManualBookingPriceDetails base={priceBase} spec={spec} setSpec={setSpec} extras={extras}
            insuranceName={plans.find((x) => x.id === f.insurance_plan_id)?.name}
            showAmountPaid={false} paidSoFar={paidSoFar} />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Status</Label>
              <select value={f.status} onChange={set("status")} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="edit-status">
                {["confirmed", "completed", "cancelled", "no_show"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Payment</Label>
              <select value={f.payment_status} onChange={set("payment_status")} className="mt-1 w-full rounded-lg border border-border px-2 py-2 text-sm" data-testid="edit-payment-status">
                {["paid", "pending", "refunded"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {f.status !== (booking.status || "confirmed") && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="status-notify-hint">
              The customer will automatically get an email & WhatsApp update about this status change.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div><Label>Customer name</Label><Input value={f.customer_name} onChange={set("customer_name")} className="mt-1" data-testid="edit-customer-name" /></div>
            <div><Label>Email</Label><Input value={f.customer_email} onChange={set("customer_email")} className="mt-1" data-testid="edit-customer-email" /></div>
            <div><Label>Phone</Label><Input value={f.customer_phone} onChange={set("customer_phone")} className="mt-1" data-testid="edit-customer-phone" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Label>Internal notes</Label><Textarea value={f.notes} onChange={set("notes")} rows={2} className="mt-1" data-testid="edit-notes" /></div>
            <div><Label>Customer notes</Label><Textarea value={f.customer_notes} onChange={set("customer_notes")} rows={2} className="mt-1" data-testid="edit-customer-notes" /></div>
          </div>

          <div>
            <button type="button" onClick={loadHistory} className="text-xs font-bold text-neutral-500 inline-flex items-center gap-1" data-testid="edit-history-btn">
              <History className="h-3 w-3" /> View edit history
            </button>
            {history && (
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 rounded-lg border border-border p-2" data-testid="edit-history-list">
                {history.length === 0 && <div className="text-xs text-neutral-400">No edits yet.</div>}
                {history.map((h, i) => (
                  <div key={i} className="text-[11px]">
                    <span className="font-bold">{h.by_name}</span> <span className="text-neutral-400">({h.by_role}) · {String(h.at).slice(0, 16).replace("T", " ")}</span>
                    {(h.changes || []).map((c, j) => (
                      <div key={j} className="text-neutral-500 pl-2">{c.field}: {JSON.stringify(c.before)} → {JSON.stringify(c.after)}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="edit-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
